/*
 * apiClient.js
 * Nubelytics – Cliente HTTP centralizado con autenticación OAuth2 (RFC 6749) y
 * manejo automático de refresh de tokens. Implementa patrón Adapter para
 * abstraer axios y exponer una interfaz limpia al resto de la aplicación.
 */

import axios from 'axios';
import TokenServiceFacade from '../auth/TokenServiceFacade';
import container from '../ioc/container';
import appConfig from '../config/AppConfig';
import { buildApiUrl } from '../../utils/apiUrlBuilder';
import CorsProxyInterceptor from '../cors/CorsProxyInterceptorSlim';
import corsProxyService from '../cors/CorsProxyService';
import userDataService from '../../services/userDataService';
import oauth2Monitor from '../monitoring/OAuth2Monitor';

/**
 * ApiClientAdapter
 * Encapsula una instancia de axios y aplica interceptores para gestionar
 * automáticamente los encabezados de autenticación según RFC 6749.
 * 
 * Implementa patrón Adapter para la biblioteca axios y aplica:
 * - Normalización centralizada de URLs mediante AppConfig
 * - Interceptores unificados para gestión de tokens
 * - Manejo automático de refresh JWT ante 401/403
 */
class ApiClientAdapter {
  /**
   * @param {Object} config        Configuración inicial para axios.create()
   * @param {AuthUseCase} authUC   Caso de uso de autenticación (inyectado)
   * @param {Object} logger        Logger (inyectado)
   */
  constructor(config = {}, authUC, logger) {
    this.logger = logger || console;
    this.authUseCase = authUC;
    this.tokenService = new TokenServiceFacade();
    
    // Detectar si hay problemas conocidos de CORS
    this._hasKnownCorsIssues = false;
    try {
      const lastCorsError = localStorage.getItem('nubelytics_last_cors_error');
      if (lastCorsError) {
        const corsData = JSON.parse(lastCorsError);
        const errorTime = new Date(corsData.timestamp || Date.now());
        const oneHour = 60 * 60 * 1000;
        
        if ((new Date() - errorTime) < oneHour) {
          this._hasKnownCorsIssues = true;
          this.logger.warn('[ApiClient] Usando modo compatibilidad CORS por errores anteriores');
        }
      }
    } catch (e) { /* Ignorar errores */ }

    // Configurar Axios con la base URL correcta, aplicando modo CORS compat si es necesario
    const baseURL = buildApiUrl('', { 
      forceCorsCompatMode: this._hasKnownCorsIssues 
    });

    // Configuración mejorada con cabeceras explícitas para forzar respuestas JSON
    this.axiosInstance = axios.create({ 
      baseURL, 
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        // Headers adicionales que ayudan con CORS
        'X-Requested-With': 'XMLHttpRequest'
      },
      // Configuración para manejo de CORS cuando se trabaja con credenciales
      withCredentials: appConfig.apiConfig?.withCredentials || false,
      ...config 
    });

    // Registrar el CorsProxyInterceptor con inyección de dependencias
    // 1) Inyectamos el servicio corsProxyService (principio de inversión de dependencias)
    // 2) Registramos los interceptores sobre la instancia de Axios recién creada
    this.corsProxy = new CorsProxyInterceptor(corsProxyService);
    this.corsProxy.register(this.axiosInstance);

    // Aplicar los interceptores estándar del ApiClient
    this.applyInterceptors();
  }

  /* ------------------------------------------------------------------ */
  /* Interceptores                                                      */
  /* ------------------------------------------------------------------ */

  /**
   * Aplica interceptores para gestión automática de tokens y errores
   */
  applyInterceptors() {
    // Interceptor de solicitudes: añadir token de autenticación
    this.axiosInstance.interceptors.request.use(async (config) => {
      try {
        // Registrar timestamp y URL para diagnóstico
        const requestTimestamp = new Date();
        
        try {
          localStorage.setItem('api_last_request_url', config.url);
          localStorage.setItem('api_last_request_timestamp', requestTimestamp.toISOString());
          localStorage.setItem('api_last_request_method', config.method?.toUpperCase() || 'GET');
        } catch (e) {/* Ignorar errores de localStorage */}
        
        // Obtener token actual o refrescarlo si es necesario usando la fachada
        const tokenInfo = await this.tokenService.getTokenInfo();
        
        if (tokenInfo && tokenInfo.token) {
          // Aplicar token según formato RFC 6749
          const alreadyPrefixed = typeof tokenInfo.token === 'string' && tokenInfo.token.startsWith(`${tokenInfo.format} `);
          config.headers.Authorization = alreadyPrefixed
            ? tokenInfo.token
            : `${tokenInfo.format} ${tokenInfo.token}`;
          
          // Log para diagnóstico
          if (appConfig.isDevelopment && appConfig.isDevelopment()) {
            this.logger.debug('[ApiClient] Añadido token a solicitud:', { 
              url: config.url,
              tokenFormat: tokenInfo.format,
              expiresIn: tokenInfo.expiresIn
            });
          }
        }

        // Añadir storeId a la cabecera si está disponible
        try {
          const storeId = userDataService.getStoreId && userDataService.getStoreId();
          if (storeId && storeId !== 'ERROR_NO_STORE_ID') {
            config.headers['X-Store-Id'] = storeId;
            if (appConfig.isDevelopment && appConfig.isDevelopment()) {
              this.logger.debug('[ApiClient] Añadido X-Store-Id a solicitud:', storeId);
            }
          }
        } catch (e) {
          this.logger.warn('[ApiClient] Error al añadir X-Store-Id:', e);
        }
      } catch (err) {
        // Capturar cualquier error del interceptor para evitar que la petición falle
        this.logger.warn('[ApiClient] Error en interceptor de solicitud:', err);
        try {
          localStorage.setItem('api_request_interceptor_error', err.message || 'Error desconocido');
          localStorage.setItem('api_request_interceptor_error_timestamp', new Date().toISOString());
        } catch (e) {/* Ignorar errores */}
      }
      
      // Agregar timestamp para medir duración de requests
      config.metadata = config.metadata || {};
      config.metadata.startTime = Date.now();
      
      return config;
    });

    // Interceptor de respuestas: manejo de errores de autenticación y refresh de tokens
    this.axiosInstance.interceptors.response.use(
      (response) => {
        // Registrar respuestas exitosas para diagnóstico
        try {
          localStorage.setItem('api_last_success_url', response.config.url);
          localStorage.setItem('api_last_success_timestamp', new Date().toISOString());
          localStorage.setItem('api_last_success_status', response.status);
        } catch (e) {/* Ignorar errores */}
        
        // Registrar solicitud exitosa en monitor
        const requestStartTime = response.config?.metadata?.startTime || Date.now();
        const duration = Date.now() - requestStartTime;
        oauth2Monitor.recordNetworkRequest(
          response.config?.url || 'unknown',
          response.status,
          duration
        );
        
        return response;
      },
      async (error) => {
        this.logger.error('[ApiClient] Error en respuesta API:', error.message || 'Error desconocido', {
          url: error.config?.url,
          status: error.response?.status,
          data: error.response?.data
        });
        
        // Diagnóstico detallado de errores de red
        try {
          localStorage.setItem('api_last_error_url', error.config?.url || 'unknown');
          localStorage.setItem('api_last_error_timestamp', new Date().toISOString());
          localStorage.setItem('api_last_error_status', String(error.response?.status || 'network-error'));
          
          if (error.response && error.response.data) {
            localStorage.setItem('api_last_error_message', 
              typeof error.response.data === 'string' 
                ? error.response.data 
                : JSON.stringify(error.response.data).substring(0, 100)
            );
          }
        } catch (e) {/* Ignorar errores */}
        
        // Registrar solicitud de red en monitor
        const requestStartTime = error.config?.metadata?.startTime || Date.now();
        const duration = Date.now() - requestStartTime;
        oauth2Monitor.recordNetworkRequest(
          error.config?.url || 'unknown',
          error.response?.status || 0,
          duration
        );
        
        // Detectar errores de autenticación para invalidar token
        if (error.response?.status === 401 || error.response?.status === 403) {
          // Error de autenticación - Invalidar token actual a través de la fachada
          this.tokenService.invalidateToken();
          this.logger.warn('[ApiClient] Token invalidado tras error de autenticación');
        }
        
        // Reintentar la solicitud después de refrescar el token
        const status = error.response?.status;
        
        // Solo procesar reintentos para errores 401/403 y si no hemos reintentado ya
        if ((status === 401 || status === 403) && !error.config._retry) {
          this.logger.warn(`[ApiClient] Error ${status} en ${error.config.url}, intentando refresh del token...`);
          error.config._retry = true;
          
          try {
            // Registro de intento de refresh de token
            localStorage.setItem('api_token_refresh_attempt', new Date().toISOString());
            localStorage.setItem('api_token_refresh_url', error.config.url);
            
            // Solicitar token fresco forzando refresh usando la fachada
            const tokenData = await this.tokenService.getTokenInfo(true);
            
            if (tokenData?.token) {
              this.logger.info('[ApiClient] Token refrescado exitosamente, reintentando petición');
              localStorage.setItem('api_token_refresh_success', 'true');
              
              // Registrar refresh exitoso en monitor
              oauth2Monitor.recordTokenRequest('refresh', true, {
                url: error.config?.url,
                status: error.response?.status
              });
              
              // Aplicar nuevo token a la petición existente y ejecutarla inmediatamente
              return this._applyTokenToRequest(error.config, tokenData, true);
            } else {
              this.logger.error('[ApiClient] No se pudo obtener token fresco');
              localStorage.setItem('api_token_refresh_failed', 'true');
              localStorage.setItem('api_token_refresh_reason', 'no-token-data');
              
              // Registrar fallo de refresh en monitor
              oauth2Monitor.recordTokenRequest('refresh', false, {
                url: error.config?.url,
                status: error.response?.status,
                reason: 'no-token-data'
              });
            }
          } catch (err) {
            this.logger.error('[ApiClient] Error refrescando token:', err);
            try {
              localStorage.setItem('api_token_refresh_error', err.message || 'Unknown error');
              localStorage.setItem('api_token_refresh_failed', 'true');
            } catch (e) {/* Ignorar errores */}
            
            // Registrar error de refresh en monitor
            oauth2Monitor.recordAuthError(err, {
              type: 'token_refresh_error',
              url: error.config?.url,
              status: error.response?.status
            });
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  /**
   * Aplica el token a la configuración de la solicitud y opcionalmente ejecuta la solicitud
   * @param {Object} config - Configuración de axios
   * @param {Object} tokenData - Datos del token {token, format}
   * @param {boolean} executeRequest - Si es true, ejecuta la solicitud inmediatamente
   * @returns {Promise|Object} - Promesa con la respuesta si executeRequest es true, o config si es false
   */
  _applyTokenToRequest(config, tokenData, executeRequest = false) {
    // Aplicar token según RFC 6750
    config.headers = config.headers || {};
    config.headers.Authorization = `${tokenData.format} ${tokenData.token}`;
    
    // Eliminar cabeceras redundantes o no estándar para mayor limpieza
    delete config.headers.Authentication;
    delete config.headers['X-Auth-Token'];
    
    // Diagnóstico para monitoreo
    try {
      localStorage.setItem('api_token_applied', 'true');
      localStorage.setItem('api_token_applied_timestamp', new Date().toISOString());
      localStorage.setItem('api_token_applied_format', tokenData.format || 'Bearer');
    } catch (e) {/* Ignorar errores */}
    
    // Si se solicita ejecutar la petición, hacerla inmediatamente
    if (executeRequest) {
      return this.axiosInstance(config);
    }
    
    return config;
  }

  /* ------------------------------------------------------------------ */
  /* Métodos públicos                                              */
  /* ------------------------------------------------------------------ */

  /**
   * Devuelve la URL base configurada para todas las peticiones API
   * Esto es crucial para diagnóstico y para que los servicios puedan
   * construir URLs absolutas correctamente según el entorno
   * @param {Object} [options] - Opciones para construir la URL
   * @param {boolean} [options.forceCorsCompatMode=false] - Forzar modo compatible con CORS
   * @return {string} URL base normalizada que incluye /api cuando es necesario
   */
  getBaseUrl(options = {}) {
    // Combinar opciones con estado interno
    const forceCorsCompatMode = options.forceCorsCompatMode || this._hasKnownCorsIssues;
    
    // Usar buildApiUrl para obtener una URL base correcta con posibles ajustes CORS
    const baseUrlWithApiSegment = buildApiUrl('', { forceCorsCompatMode });
    
    // Registrar diagnóstico
    if (appConfig.debug) {
      try {
        localStorage.setItem('nubelytics_api_client_getBaseUrl', baseUrlWithApiSegment);
        localStorage.setItem('nubelytics_api_client_raw_baseURL', this.axiosInstance.defaults.baseURL);
        localStorage.setItem('nubelytics_cors_compat_active', String(forceCorsCompatMode));
      } catch (e) {/* Error no crítico */}
    }
    
    return baseUrlWithApiSegment;
  }

  /**
   * Método GET con soporte para estrategias anti-CORS
   * @param {string} url - URL a la que hacer la petición
   * @param {Object} config - Configuración adicional
   * @return {Promise} Promesa con la respuesta
   */
  get(url, config = {}) {
    // Si hay problemas CORS conocidos, usar el servicio de proxy
    if (this._hasKnownCorsIssues) {
      this.logger.debug('[ApiClient] Usando CorsProxyService para GET:', url);
      return corsProxyService.request({
        ...config,
        method: 'GET',
        url: url
      });
    }
    return this.axiosInstance.get(url, config);
  }

  /**
   * Método POST con soporte para estrategias anti-CORS
   * @param {string} url - URL a la que hacer la petición
   * @param {Object} data - Datos a enviar
   * @param {Object} config - Configuración adicional
   * @return {Promise} Promesa con la respuesta
   */
  post(url, data, config = {}) {
    // Si hay problemas CORS conocidos, usar el servicio de proxy
    if (this._hasKnownCorsIssues) {
      this.logger.debug('[ApiClient] Usando CorsProxyService para POST:', url);
      return corsProxyService.request({
        ...config,
        method: 'POST',
        url: url,
        data
      });
    }
    return this.axiosInstance.post(url, data, config);
  }

  /**
   * Método PUT con soporte para estrategias anti-CORS
   * @param {string} url - URL a la que hacer la petición
   * @param {Object} data - Datos a enviar
   * @param {Object} config - Configuración adicional
   * @return {Promise} Promesa con la respuesta
   */
  put(url, data, config = {}) {
    // Si hay problemas CORS conocidos, usar el servicio de proxy
    if (this._hasKnownCorsIssues) {
      this.logger.debug('[ApiClient] Usando CorsProxyService para PUT:', url);
      return corsProxyService.request({
        ...config,
        method: 'PUT',
        url: url,
        data
      });
    }
    return this.axiosInstance.put(url, data, config);
  }

  /**
   * Método PATCH con soporte para estrategias anti-CORS
   * @param {string} url - URL a la que hacer la petición
   * @param {Object} data - Datos a enviar
   * @param {Object} config - Configuración adicional
   * @return {Promise} Promesa con la respuesta
   */
  patch(url, data, config = {}) {
    // Si hay problemas CORS conocidos, usar el servicio de proxy
    if (this._hasKnownCorsIssues) {
      this.logger.debug('[ApiClient] Usando CorsProxyService para PATCH:', url);
      return corsProxyService.request({
        ...config,
        method: 'PATCH',
        url: url,
        data
      });
    }
    return this.axiosInstance.patch(url, data, config);
  }

  /**
   * Método DELETE con soporte para estrategias anti-CORS
   * @param {string} url - URL a la que hacer la petición
   * @param {Object} config - Configuración adicional
   * @return {Promise} Promesa con la respuesta
   */
  delete(url, config = {}) {
    // Si hay problemas CORS conocidos, usar el servicio de proxy
    if (this._hasKnownCorsIssues) {
      this.logger.debug('[ApiClient] Usando CorsProxyService para DELETE:', url);
      return corsProxyService.request({
        ...config,
        method: 'DELETE',
        url: url
      });
    }
    return this.axiosInstance.delete(url, config);
  }
}

// Exportar singleton listo para usar.
const apiClient = new ApiClientAdapter({}, container.authUseCase, container.logger);
export default apiClient;
